/**
 * useSupplierReturns Hooks
 *
 * React Query hooks for supplier returns.
 * @see US-40.3 - Frontend retours fournisseur
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  supplierReturnsService,
  type QuerySupplierReturnsDto,
  type CreateSupplierReturnDto,
  type UpdateSupplierReturnDto,
} from '../services/supplier-returns.service';

/**
 * Query key factory for supplier returns
 */
export const supplierReturnKeys = {
  all: ['supplier-returns'] as const,
  lists: () => [...supplierReturnKeys.all, 'list'] as const,
  list: (params: QuerySupplierReturnsDto) => [...supplierReturnKeys.lists(), params] as const,
  details: () => [...supplierReturnKeys.all, 'detail'] as const,
  detail: (id: string) => [...supplierReturnKeys.details(), id] as const,
};

/**
 * Hook to fetch supplier returns list with pagination
 */
export function useSupplierReturns(params: QuerySupplierReturnsDto) {
  return useQuery({
    queryKey: supplierReturnKeys.list(params),
    queryFn: () => supplierReturnsService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single supplier return
 */
export function useSupplierReturn(id: string | null) {
  return useQuery({
    queryKey: supplierReturnKeys.detail(id!),
    queryFn: () => supplierReturnsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a supplier return
 */
export function useCreateSupplierReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSupplierReturnDto) =>
      supplierReturnsService.create(data),
    onSuccess: (ret) => {
      queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
      toast.success(`Retour ${ret.returnNumber} cree`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a supplier return
 */
export function useUpdateSupplierReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierReturnDto }) =>
      supplierReturnsService.update(id, data),
    onSuccess: (ret) => {
      queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
      toast.success(`Retour ${ret.returnNumber} mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to delete a supplier return
 */
export function useDeleteSupplierReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => supplierReturnsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
      toast.success('Retour supprime');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to validate a supplier return
 */
export function useValidateSupplierReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => supplierReturnsService.validate(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
      toast.success(`Retour valide — ${result.movementCount} mouvement(s) cree(s)`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la validation');
    },
  });
}

/**
 * Hook to cancel a supplier return
 */
export function useCancelSupplierReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      supplierReturnsService.cancel(id, reason),
    onSuccess: (ret) => {
      queryClient.invalidateQueries({ queryKey: supplierReturnKeys.all });
      toast.success(`Retour ${ret.returnNumber} annule`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'annulation");
    },
  });
}
